import {
  alerteNomFacture,
  carteValide,
  cloisonDe,
  decisionPayeur,
  formaterJour,
  formaterMontant,
  libelleCarte,
} from '@admin-pmvb/core'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { api, corps, ecrit, type FichePiece, lire, requeteMoi, requetes } from '../api'
import { DecisionsAcheminement } from './Acheminement'
import { ClasserPiece } from './ClasserPiece'
import { Formulaire } from './Formulaire'
import { Vide } from './Section'
import { confirmer } from './confirmation'

// « Qui a payé, à qui refacturer » (facture d'un portail d'achats, packages/core/src/cartes.ts) :
// le payeur proposé par la carte (l'entité où la facture est entrée), retenir la carte pour les
// suivantes, puis à qui la refacturer, ou à personne. La pièce va chez l'entité dont c'est la
// charge : payée par une autre de nos entités, c'est une avance, avec sa ligne « à refacturer » ;
// refacturée à un tiers, elle reste chez qui a payé. Une seule tâche par facture : ses autres
// décisions (classer, vérifier) en sont des sous-décisions, tranchées ici une fois le payeur dit,
// dans l'entité qui garde la pièce.

const OUVERTES = ['a_faire', 'en_cours', 'en_attente_tiers']

/** Reste-t-il à classer la pièce (sous-décision ouverte) ? */
const aClasser = (p: Pick<FichePiece, 'taches'>) =>
  p.taches.some((t) => t.action === 'choisir_rubrique' && OUVERTES.includes(t.statut))

export function DecisionPayeur({ pieceId, apres }: { pieceId: string; apres: () => void }) {
  const client = useQueryClient()
  const moi = useQuery(requeteMoi).data
  const piece = useQuery(requetes.piece(pieceId))
  const entites = useQuery(requetes.entites).data ?? []
  const tiers = useQuery(requetes.tiers).data ?? []
  const [saisie, setSaisie] = useState<Record<string, string | boolean>>({})
  const p = piece.data
  if (!p) return <Vide>{piece.isPending ? 'Chargement…' : 'Pièce introuvable.'}</Vide>

  const carte = carteValide({ reseau: p.carteReseau, fin: p.carteFin })
  const privees = entites.filter((e) => e.privee).map((e) => e.id)
  const cote = cloisonDe(p.entiteId, privees)
  // Payeur et destinataire : nos entités où l'on écrit, du même côté que la pièce.
  const ecrivables = entites.filter(
    (e) => !e.archivee && moi && ecrit(moi, e.id) && cloisonDe(e.id, privees) === cote,
  )
  const montant = p.montants?.ttc ?? p.montants?.aPayer ?? null
  const quoi = [
    p.emetteur ?? p.expediteur,
    p.dateDocument && formaterJour(p.dateDocument),
    montant !== null && formaterMontant(montant, p.devise),
  ]
    .filter(Boolean)
    .join(' · ')
  const enLecture = p.statut === 'recue' || p.statut === 'extraite'
  // Chez qui ira la pièce, d'après ce qui est saisi : refacturée à une entité, chez elle ; sinon,
  // chez qui a payé. La facture au nom d'une autre : à signaler avant de valider, sans bloquer.
  const destinataire = typeof saisie.destinataire === 'string' ? saisie.destinataire : ''
  const payeurId = typeof saisie.entiteId === 'string' ? saisie.entiteId : p.entiteId
  const chargeId = destinataire.startsWith('e:') ? destinataire.slice(2) : payeurId
  const charge = entites.find((e) => e.id === chargeId)
  const alerte = alerteNomFacture({
    nomSurLaFacture: p.nomSurLaFacture,
    charge: charge ? { entiteId: charge.id, nom: charge.nom } : null,
  })

  // Payeur dit : ce qui reste de la pièce (la classer dans son entité, un envoi à trancher).
  if (!p.payeurAttendu)
    return (
      <>
        <p className="discret">
          Payeur indiqué : la pièce est chez {p.entite ?? 'son entité'}. Reste à la classer ; la
          tâche se fermera ensuite.
        </p>
        {aClasser(p) && <ClasserPiece p={p} ecriture />}
        <DecisionsAcheminement p={p} />
      </>
    )

  return (
    <>
      <p>
        <strong>{quoi || p.nomOriginal || 'Facture'}</strong>
        <br />
        <span className="discret">
          {carte
            ? `Payée avec la carte ${libelleCarte(carte)}.`
            : 'Carte de paiement non lue sur le portail.'}{' '}
          La pièce ira chez l’entité dont c’est la dépense ; payée par une autre, c’est une avance
          qu’elle lui rembourse (ligne « à refacturer »).
        </span>
      </p>
      {aClasser(p) && (
        <p className="discret">
          Le classement suit, une fois le payeur dit, dans l’entité qui garde la pièce.
        </p>
      )}
      {enLecture ? (
        <p className="discret">Pièce en cours de lecture : la décision se prend une fois lue.</p>
      ) : (
        <Formulaire
          surSaisie={setSaisie}
          champs={[
            {
              nom: 'entiteId',
              libelle: 'Qui a payé',
              type: 'choix',
              requis: true,
              options: ecrivables.map((e) => ({ valeur: e.id, libelle: e.nom })),
            },
            ...(carte
              ? [
                  {
                    nom: 'retenirCarte',
                    libelle: `Retenir : la carte ${libelleCarte(carte)} est celle de cette entité`,
                    type: 'booleen' as const,
                    aide: 'Ses autres factures en attente vont aussi chez cette entité, payeur proposé.',
                  },
                ]
              : []),
            {
              nom: 'destinataire',
              libelle: 'À refacturer à',
              type: 'choix',
              aide: 'Vide : à personne, la dépense est celle de qui a payé.',
              options: [
                ...ecrivables.map((e) => ({ valeur: `e:${e.id}`, libelle: e.nom })),
                ...tiers.map((t) => ({ valeur: `t:${t.id}`, libelle: t.nom })),
              ],
            },
            {
              nom: 'montantCts',
              libelle: 'Montant à refacturer (€)',
              type: 'montant',
              aide: 'Vide : tout le montant de la facture.',
            },
            { nom: 'motif', libelle: 'Motif', type: 'texte' },
          ]}
          initial={{ entiteId: p.entiteId, retenirCarte: true }}
          libelleBouton="Valider"
          onValider={async (v) => {
            const [sorte, id] = v.destinataire ? String(v.destinataire).split(':') : []
            const apresDecision = await lire(
              api.pieces[':id'].payeur.$post({
                param: { id: pieceId },
                json: corps(decisionPayeur, {
                  entiteId: v.entiteId,
                  retenirCarte: carte ? v.retenirCarte === true : false,
                  ...(sorte === 'e' && { destinataireEntiteId: id }),
                  ...(sorte === 't' && { destinataireTiersId: id }),
                  ...(sorte && v.montantCts !== null && { montantCts: v.montantCts }),
                  ...(sorte && { motif: v.motif }),
                }),
              }),
            )
            await client.invalidateQueries()
            // Encore à classer : la tâche reste ouverte, on y reste.
            if (!aClasser(apresDecision)) apres()
          }}
        />
      )}
      {alerte && <p className="alerte">{alerte}</p>}
      <div className="boutons">
        <a
          href={`/api/pieces/${pieceId}/fichier`}
          target="_blank"
          rel="noreferrer"
          className="bouton-discret"
          title="Le document tel qu’il a été reçu"
        >
          Voir le PDF
        </a>
        <Link
          to="/pieces/$id"
          params={{ id: pieceId }}
          className="bouton-discret"
          title="La pièce dans l’appli : lecture, montants, paiement, classement"
        >
          Fiche de la pièce
        </Link>
      </div>
    </>
  )
}

/** Tableau des cartes : chacune et l'entité qui paie avec ; une carte se retire. */
export function TableauCartes() {
  const client = useQueryClient()
  const moi = useQuery(requeteMoi).data
  const cartes = useQuery(requetes.cartes).data ?? []
  if (cartes.length === 0) return null
  return (
    <section className="carte carte-flush">
      <div className="barre-selection">
        <strong>Cartes de paiement</strong>
        <span className="discret">
          Retenues par la décision « qui a payé » : elles proposent le payeur des factures
          suivantes.
        </span>
      </div>
      {cartes.map((c) => {
        const carte = carteValide(c)
        return (
          <div key={c.id} className="ligne">
            <div className="ligne-contenu">
              <span className="ligne-titre">
                <strong>{carte ? libelleCarte(carte) : c.fin}</strong>
                {c.libelle && <span className="discret">{c.libelle}</span>}
              </span>
              <span className="discret">Paie pour {c.entite}</span>
            </div>
            {moi && ecrit(moi, c.entiteId) && (
              <button
                type="button"
                className="bouton-discret"
                onClick={async () => {
                  if (!(await confirmer('Retirer cette carte du tableau ?'))) return
                  await lire(api.cartes[':id'].$delete({ param: { id: c.id } }))
                  await client.invalidateQueries({ queryKey: ['cartes'] })
                }}
              >
                Retirer
              </button>
            )}
          </div>
        )
      })}
    </section>
  )
}
